import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { getUserCompany } from "../api/user";
import { CompanyInfo } from "../types/companyTypes";

interface CompanyState {
  selectedUserCompany: CompanyInfo | null;
  loading: boolean;
  error: string | null;
}

const initialState: CompanyState = {
  selectedUserCompany: null,
  loading: false,
  error: null,
};

export const fetchUserCompany = createAsyncThunk<
  CompanyInfo,
  { company_id?: number | string; token?: string },
  { rejectValue: string }
>(
  "company/fetchUserCompany",
  async ({ company_id = "", token }: { company_id?: number | string; token?: string }, { rejectWithValue }) => {
    try {
      // Pass the token to the getUserCompany function
      const data: CompanyInfo = await getUserCompany(company_id || "", token);
      console.log("Fetched user company data:", data);
      return data;
    } catch (err: unknown) {
      const error = err as Error;
      return rejectWithValue(error.message);
    }
  }
);

const companySlice = createSlice({
  name: "company",
  initialState,
  reducers: {
    resetCompanyState: (state) => {
      state.selectedUserCompany = null;
      state.loading = false;
      state.error = null;
      localStorage.removeItem("selectedUserCompany");
    },
    setCompanyFromCache: (state, action: PayloadAction<CompanyInfo>) => {
      state.selectedUserCompany = action.payload;
    },
    updateCompanyEmail: (state, action: PayloadAction<string>) => {
      if (state.selectedUserCompany) {
        state.selectedUserCompany.companyEmail = action.payload;
        localStorage.setItem("selectedUserCompany", JSON.stringify(state.selectedUserCompany));
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchUserCompany.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchUserCompany.fulfilled, (state, action: PayloadAction<CompanyInfo>) => {
        state.loading = false;
        state.selectedUserCompany = action.payload;
        localStorage.setItem("selectedUserCompany", JSON.stringify(action.payload));
      })
      .addCase(fetchUserCompany.rejected, (state, action: PayloadAction<string | undefined>) => {
        state.loading = false;
        state.error = action.payload || null;
      });
  },
});

export const { resetCompanyState, setCompanyFromCache, updateCompanyEmail } = companySlice.actions;
export default companySlice.reducer;
